/**
 * sharedStyles.js — Reusable MUI sx-prop factories and style constants.
 *
 * Every colour is sourced from the global palette exported by
 * src/theme/palette.js so the admin panel stays visually consistent.
 *
 * Usage:
 *   import { statusBadgeSx, kpiCardSx } from '../common/sharedStyles'
 *   <Chip sx={statusBadgeSx('success')} label="Delivered" />
 *   <Paper sx={kpiCardSx('primary')}>…</Paper>
 */
import palette from '../../theme/palette';

/* ───────────────────────── Status Badge ───────────────────────── */

const STATUS_MAP = {
  // Open / Pending / Reported / Warning
  open:     { bg: palette.warning.lighter, color: palette.warning.dark,  border: palette.warning.light },
  pending:  { bg: palette.warning.lighter, color: palette.warning.dark,  border: palette.warning.light },
  warning:  { bg: palette.warning.lighter, color: palette.warning.dark,  border: palette.warning.light },
  reported: { bg: palette.warning.lighter, color: palette.warning.dark,  border: palette.warning.light },

  // Closed / Delivered / Success
  closed:    { bg: palette.success.lighter, color: palette.success.dark,  border: palette.success.light },
  delivered: { bg: palette.success.lighter, color: palette.success.dark,  border: palette.success.light },
  success:   { bg: palette.success.lighter, color: palette.success.dark,  border: palette.success.light },
  completed: { bg: palette.success.lighter, color: palette.success.dark,  border: palette.success.light },

  // Failed / RTO / Error
  failed: { bg: palette.error.lighter, color: palette.error.dark, border: palette.error.light },
  rto:    { bg: palette.error.lighter, color: palette.error.dark, border: palette.error.light },
  error:  { bg: palette.error.lighter, color: palette.error.dark, border: palette.error.light },

  // In-Progress / Initiated / Info
  initiated:   { bg: palette.info.lighter,  color: palette.info.dark,  border: palette.info.light },
  in_progress: { bg: palette.info.lighter,  color: palette.info.dark,  border: palette.info.light },
  info:        { bg: palette.info.lighter,  color: palette.info.dark,  border: palette.info.light },

  // Neutral / default
  neutral: { bg: palette.grey[200], color: palette.grey[600], border: palette.grey[300] },
};

/**
 * Returns MUI sx props for a status badge / chip.
 * @param {string} status — one of the keys in STATUS_MAP, or any custom string
 *                           (falls back to 'neutral').
 * @returns {object} sx-compatible style object
 */
export function statusBadgeSx(status) {
  const key = (status || '').toLowerCase().replace(/[\s-]/g, '_');
  const tokens = STATUS_MAP[key] || STATUS_MAP.neutral;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    px: 1,
    py: 0.25,
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 700,
    lineHeight: 1.5,
    bgcolor: tokens.bg,
    color: tokens.color,
    border: `1px solid ${tokens.border}`,
  };
}

/**
 * Resolves a status string to palette-consistent colours.
 * Useful when you need raw colour values (e.g. for chart segments)
 * rather than a full sx object.
 * @param {string} status
 * @returns {{ bg: string, color: string, border: string }}
 */
export function statusColors(status) {
  const key = (status || '').toLowerCase().replace(/[\s-]/g, '_');
  return STATUS_MAP[key] || STATUS_MAP.neutral;
}

/* ───────────────────────── KPI Card ───────────────────────────── */

const ACCENT_MAP = {
  primary:   { accent: palette.primary.main,   bg: palette.primary.lighter },
  success:   { accent: palette.success.main,   bg: palette.success.lighter },
  error:     { accent: palette.error.main,     bg: palette.error.lighter },
  warning:   { accent: palette.warning.main,   bg: palette.warning.lighter },
  info:      { accent: palette.info.main,      bg: palette.info.lighter },
  secondary: { accent: palette.secondary.main, bg: palette.secondary.lighter },
};

/**
 * Returns sx props for a KPI stat card (Paper).
 * @param {string} colorToken — 'primary' | 'success' | 'error' | 'warning' | 'info' | 'secondary'
 * @returns {object}
 */
export function kpiCardSx(colorToken = 'primary') {
  const t = ACCENT_MAP[colorToken] || ACCENT_MAP.primary;
  return {
    p: 2.5,
    borderRadius: 2,
    border: `1px solid ${palette.grey[300]}`,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: 4,
      height: '100%',
      bgcolor: t.accent,
    },
  };
}

/**
 * Returns the accent colour for a given colour token.
 * @param {string} colorToken
 * @returns {string} hex colour string
 */
export function accentColor(colorToken = 'primary') {
  return (ACCENT_MAP[colorToken] || ACCENT_MAP.primary).accent;
}

/* ───────────────────────── KPI Row Grid ──────────────────────── */

/**
 * CSS Grid props for a responsive KPI card row.
 */
export const kpiRowSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 2,
  mb: 3,
};

/* ───────────────────────── Filter Buttons ─────────────────────── */

/**
 * @param {'apply' | 'reset'} variant
 */
export function filterButtonSx(variant = 'apply') {
  if (variant === 'reset') {
    return {
      bgcolor: palette.error.main,
      color: palette.error.contrastText,
      fontWeight: 600,
      textTransform: 'none',
      '&:hover': { bgcolor: palette.error.dark },
    };
  }
  return {
    bgcolor: palette.primary.main,
    color: palette.primary.contrastText,
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': { bgcolor: palette.primary.dark },
  };
}

/* ───────────────────────── AWB / Link ─────────────────────────── */

export const awbLinkSx = {
  color: palette.primary.main,
  fontWeight: 600,
  fontFamily: 'monospace',
  fontSize: '0.8rem',
  textDecoration: 'none',
  cursor: 'pointer',
  '&:hover': { color: palette.primary.dark, textDecoration: 'underline' },
};

/* ───────────────────────── Table Head ─────────────────────────── */

export const tableHeadSx = {
  fontWeight: 700,
  fontSize: '0.8rem',
  color: palette.text.secondary,
  bgcolor: palette.grey[200],
};

/* ───────────────────────── Chart Container ────────────────────── */

export const chartContainerSx = {
  bgcolor: palette.background.paper,
  border: `1px solid ${palette.grey[300]}`,
  borderRadius: 2,
  p: 2.5,
  mb: 2.5,
};

/* ───────────────────────── Two-Col Grid ──────────────────────── */

export const twoColGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
  gap: 2.5,
  mb: 2.5,
};

/* ───────────────────────── Chart Palette ──────────────────────── */

/**
 * Standard chart colour arrays from the global palette.
 * Use these for ApexCharts `colors` option.
 */
export const chartColors = palette.chart;

/**
 * A flat list of 8 distinct chart colours for generic multi-series charts.
 * Picks the first shade from each palette.chart family plus extras.
 */
export const chartSeriesColors = [
  palette.chart.blue[0],    // #2D99FF
  palette.chart.green[0],   // #2CD9C5
  palette.chart.violet[0],  // #826AF9
  palette.chart.yellow[0],  // #FFE700
  palette.chart.red[0],     // #FF6C40
  palette.primary.main,     // #2065D1
  palette.success.main,     // #10B981
  palette.warning.main,     // #FFC107
];

/* ───────────────────────── Export button ──────────────────────── */

export const exportButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  borderColor: palette.grey[400],
  color: palette.text.primary,
  '&:hover': { borderColor: palette.grey[600], bgcolor: palette.grey[200] },
};
